'use client';

import { c as _c } from "react/compiler-runtime";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { getImageProxyUrl } from '@/shared/lib/proxy-url';
import styles from './SampleListCarousel.module.scss';
import type { SampleTemplate } from './samplePreviewTypes';
export type SampleListCarouselHandle = {
  prev: () => void;
  next: () => void;
};
type SampleListCarouselProps = {
  items: SampleTemplate[];
  onSelect: (item: SampleTemplate) => void;
  paused?: boolean;
};
type CarouselThumb = {
  key: string;
  order: number;
  source: SampleTemplate;
};
const AUTO_SCROLL_SPEED = 26.4;
const PAUSE_AFTER_MANUAL_MS = 3000;
const LOOP_COUNT = 10;
const COPIES = 4;
const TOTAL_THUMBS = LOOP_COUNT * COPIES;
const DEFAULT_CARD_WIDTH = 240;
const DEFAULT_CARD_GAP = 16;
const DEFAULT_CARD_SPAN = DEFAULT_CARD_WIDTH + DEFAULT_CARD_GAP;
function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}
function mod(value: number, divisor: number) {
  return (value % divisor + divisor) % divisor;
}
function getNearestTargetOffset(currentOffset: number, targetIndex: number, totalSpan: number, cardSpan: number) {
  const targetBaseOffset = targetIndex * cardSpan;
  const cycle = Math.round((currentOffset - targetBaseOffset) / totalSpan);
  return cycle * totalSpan + targetBaseOffset;
}
export const SampleListCarousel = forwardRef<SampleListCarouselHandle, SampleListCarouselProps>(function SampleListCarousel(t0, ref) {
  const $ = _c(23);
  const {
    items,
    onSelect,
    paused: t1
  } = t0;
  const paused = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== items) {
    t2 = Array.from({
      length: LOOP_COUNT
    }, (_, index) => items[index % items.length]);
    $[0] = items;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const loopItems = t2;
  let t3;
  if ($[2] !== loopItems) {
    t3 = Array.from({
      length: TOTAL_THUMBS
    }, (__0, index_0) => ({
      key: `sample-thumb-${index_0}`,
      order: index_0,
      source: loopItems[index_0 % LOOP_COUNT]
    }));
    $[2] = loopItems;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const thumbs = t3;
  const initialOffset = LOOP_COUNT * DEFAULT_CARD_SPAN * 1.5;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = Math.round(initialOffset / DEFAULT_CARD_SPAN);
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const initialCenteredOrder = t4;
  const rootRef = useRef(null);
  const trackRef = useRef(null);
  const cardSpanRef = useRef(DEFAULT_CARD_SPAN);
  const offsetRef = useRef(initialOffset);
  const manualTargetOffsetRef = useRef(null);
  const pausedUntilRef = useRef(0);
  const lastTimeRef = useRef(null);
  const centeredOrderRef = useRef(initialCenteredOrder);
  const [centeredOrder, setCenteredOrder] = useState(initialCenteredOrder);
  const [hoveredKey, setHoveredKey] = useState(null);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = offset => {
      if (trackRef.current) {
        trackRef.current.style.transform = `translate3d(${-offset}px, -50%, 0)`;
      }
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const applyTrackTransform = t5;
  let t6;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      const root = rootRef.current;
      if (!root) {
        return;
      }
      const readLayout = () => {
        const cs = window.getComputedStyle(root);
        const cardW = parseFloat(cs.getPropertyValue("--card-w")) || DEFAULT_CARD_WIDTH;
        const cardGap = parseFloat(cs.getPropertyValue("--card-gap")) || DEFAULT_CARD_GAP;
        const nextSpan = cardW + cardGap;
        const prevSpan = cardSpanRef.current;
        if (Math.abs(nextSpan - prevSpan) < 0.5) {
          return;
        }
        const ratio = nextSpan / prevSpan;
        offsetRef.current = offsetRef.current * ratio;
        if (manualTargetOffsetRef.current !== null) {
          manualTargetOffsetRef.current = manualTargetOffsetRef.current * ratio;
        }
        cardSpanRef.current = nextSpan;
        applyTrackTransform(offsetRef.current);
        const nextCenteredOrder = Math.round(offsetRef.current / nextSpan);
        if (nextCenteredOrder !== centeredOrderRef.current) {
          centeredOrderRef.current = nextCenteredOrder;
          setCenteredOrder(nextCenteredOrder);
        }
      };
      readLayout();
      const observer = new ResizeObserver(readLayout);
      observer.observe(root);
      return () => observer.disconnect();
    };
    t7 = [applyTrackTransform];
    $[6] = t6;
    $[7] = t7;
  } else {
    t6 = $[6];
    t7 = $[7];
  }
  useLayoutEffect(t6, t7);
  let t8;
  let t9;
  if ($[8] !== paused) {
    t8 = () => {
      if (paused) {
        return;
      }
      let rafId = 0;
      const animate = time => {
        const lastTime = lastTimeRef.current ?? time;
        const deltaMs = time - lastTime;
        lastTimeRef.current = time;
        const cardSpan = cardSpanRef.current;
        const totalSpan = LOOP_COUNT * cardSpan;
        let nextOffset = offsetRef.current;
        const manualTarget = manualTargetOffsetRef.current;
        if (manualTarget !== null) {
          const distance = manualTarget - nextOffset;
          const easedStep = distance * clamp(deltaMs / 240, 0.08, 0.22);
          nextOffset = nextOffset + easedStep;
          if (Math.abs(distance) < 0.8) {
            nextOffset = manualTarget;
            manualTargetOffsetRef.current = null;
            pausedUntilRef.current = time + PAUSE_AFTER_MANUAL_MS;
          }
        } else {
          if (time >= pausedUntilRef.current) {
            nextOffset = nextOffset + AUTO_SCROLL_SPEED * deltaMs / 1000;
          }
        }
        if (nextOffset >= totalSpan * 3.5) {
          nextOffset = nextOffset - totalSpan;
        }
        if (nextOffset <= totalSpan * 0.5) {
          nextOffset = nextOffset + totalSpan;
        }
        offsetRef.current = nextOffset;
        applyTrackTransform(nextOffset);
        const nextCenteredOrder_0 = Math.round(nextOffset / cardSpan);
        if (nextCenteredOrder_0 !== centeredOrderRef.current) {
          centeredOrderRef.current = nextCenteredOrder_0;
          setCenteredOrder(nextCenteredOrder_0);
        }
        rafId = window.requestAnimationFrame(animate);
      };
      rafId = window.requestAnimationFrame(animate);
      return () => {
        window.cancelAnimationFrame(rafId);
        lastTimeRef.current = null;
      };
    };
    t9 = [applyTrackTransform, paused];
    $[8] = paused;
    $[9] = t8;
    $[10] = t9;
  } else {
    t8 = $[9];
    t9 = $[10];
  }
  useEffect(t8, t9);
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = targetOrder => {
      const cardSpan_0 = cardSpanRef.current;
      const totalSpan_0 = LOOP_COUNT * cardSpan_0;
      manualTargetOffsetRef.current = getNearestTargetOffset(offsetRef.current, mod(targetOrder, LOOP_COUNT), totalSpan_0, cardSpan_0);
      pausedUntilRef.current = 0;
    };
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const moveToIndex = t10;
  let t11;
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => ({
      prev: () => moveToIndex(centeredOrderRef.current - 1),
      next: () => moveToIndex(centeredOrderRef.current + 1)
    });
    t12 = [moveToIndex];
    $[12] = t11;
    $[13] = t12;
  } else {
    t11 = $[12];
    t12 = $[13];
  }
  useImperativeHandle(ref, t11, t12);
  const rootClassName = paused ? `${styles.root} ${styles.rootPaused}` : styles.root;
  let t13;
  if ($[14] !== centeredOrder || $[15] !== hoveredKey || $[16] !== onSelect || $[17] !== paused || $[18] !== thumbs) {
    t13 = <div className={styles.stage}><div ref={trackRef} className={styles.track}>{thumbs.map(thumb => {
          const isCentered = !paused && thumb.order === centeredOrder;
          const isHovered = !paused && hoveredKey === thumb.key;
          const isHighlighted = isHovered || !hoveredKey && isCentered;
          const className = [styles.thumb, isCentered ? styles.thumbCentered : "", isHighlighted ? styles.thumbHighlighted : "", isHovered ? styles.thumbHovered : ""].filter(Boolean).join(" ");
          return <button key={thumb.key} type="button" onClick={() => onSelect(thumb.source)} onMouseEnter={() => setHoveredKey(thumb.key)} onMouseLeave={() => setHoveredKey(current => current === thumb.key ? null : current)} className={className} style={{
            left: `calc(${thumb.order} * var(--card-span) - var(--card-w) / 2)`
          }}><span aria-hidden="true" className={styles.thumbGlow} /><div className={styles.thumbInner}><img src={getImageProxyUrl({
                src: thumb.source.thumbnailImage ?? "/intro/pdp_thum_1.png"
              })} alt={thumb.source.title} width={382} height={573} loading="lazy" decoding="async" className={styles.thumbImage} /></div></button>;
        })}</div></div>;
    $[14] = centeredOrder;
    $[15] = hoveredKey;
    $[16] = onSelect;
    $[17] = paused;
    $[18] = thumbs;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== rootClassName || $[21] !== t13) {
    t14 = <div ref={rootRef} className={rootClassName}>{t13}</div>;
    $[20] = rootClassName;
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  return t14;
});